import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { publicAPI } from '../services/apiService';
import { locationsManager } from '../utils/locationsManager';

// Contact phone number
const PHONE_NUMBER = '+49 176 7670 5262';

const Header = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isStandorteOpen, setIsStandorteOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [siteName, setSiteName] = useState('FotografX');

  useEffect(() => {
    loadSiteBranding();
  }, []);

  const loadSiteBranding = async () => {
    try {
      const response = await publicAPI.getSiteBranding();
      setSiteName(response.data.site_name);
    } catch (error) {
      console.error('Error loading site branding:', error);
    }
  };

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.portfolio'), href: '/portfolio' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.reviews'), href: '/reviews' },
    { name: t('nav.blog'), href: '/blog' },
    { name: t('nav.contact'), href: '/contact' },
    { name: t('nav.booking'), href: '/booking' }
  ];

  const [cityLocations, setCityLocations] = useState([]);

  // Load locations from API and manager
  useEffect(() => {
    const loadLocations = async () => {
      try {
        // Initialize locations from API first
        await locationsManager.initializeDefaultLocations();
        
        // Then get active locations
        const activeLocations = locationsManager.getActiveLocations();
        const menuItems = activeLocations.map(loc => ({
          name: loc.name,
          href: `/${loc.slug}`
        }));
        setCityLocations(menuItems);
      } catch (error) {
        console.error('Error loading locations in Header:', error);
        // Fallback to localStorage data
        const activeLocations = locationsManager.getActiveLocations();
        const menuItems = activeLocations.map(loc => ({
          name: loc.name,
          href: `/${loc.slug}`
        }));
        setCityLocations(menuItems);
      }
    };

    // Load initially
    loadLocations();

    // Listen for locations updates
    const handleLocationsUpdate = () => {
      loadLocations();
    };
    
    window.addEventListener('locations-updated', handleLocationsUpdate);

    return () => {
      window.removeEventListener('locations-updated', handleLocationsUpdate);
    };
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="text-xl font-bold text-white hover:text-gray-300 transition-colors duration-200"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {siteName.toUpperCase()}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 pr-4">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium uppercase tracking-wide transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-white border-b border-white pb-1'
                    : 'text-gray-300 hover:text-white'
                }`}
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Standorte Dropdown - Fixed for all devices */}
            <div className="relative">
              <button
                onClick={() => setIsStandorteOpen(!isStandorteOpen)}
                className="flex items-center text-sm font-medium uppercase tracking-wide text-gray-300 hover:text-white transition-colors duration-200"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {currentLang === 'de' ? 'Standorte' : 'Локации'}
                <ChevronDown className={`ml-1 h-4 w-4 transform transition-transform duration-200 ${isStandorteOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isStandorteOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-800 shadow-lg rounded-md overflow-hidden z-50">
                  {cityLocations.map((city) => (
                    <Link
                      key={city.href}
                      to={city.href}
                      onClick={() => setIsStandorteOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      {city.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            {/* Phone Number */}
            <a 
              href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
              className="hidden lg:flex items-center text-sm text-gray-300 hover:text-white transition-colors duration-200 whitespace-nowrap"
            >
              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{PHONE_NUMBER}</span>
            </a>
            
            <LanguageSwitcher />
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-3 pr-2">
            {/* Phone icon for mobile */}
            <a 
              href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
              className="text-gray-300 hover:text-white transition-colors duration-200 p-1"
            >
              <Phone className="h-5 w-5" />
            </a>
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-gray-300 transition-colors duration-200 p-1"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-black border-t border-gray-800">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium uppercase tracking-wide transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-white bg-gray-900'
                    : 'text-gray-300 hover:text-white hover:bg-gray-900'
                }`}
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Mobile Standorte Section */}
            <div className="px-3 py-2">
              <div className="text-sm font-medium uppercase tracking-wide text-gray-400 mb-2" style={{ fontFamily: '"Times New Roman", serif' }}>
                {currentLang === 'de' ? 'Standorte' : 'Локации'}
              </div>
              {cityLocations.map((city) => (
                <Link
                  key={city.href}
                  to={city.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-1 text-sm text-gray-300 hover:text-white transition-colors duration-200"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;